/**
 * Per-run token cost. Founder-visible only — the brief files it under
 * "internal".
 *
 * The server does the deciding: `Run.cost` is filled for a founder and `null`
 * for everyone else. This component therefore has exactly one rule, and it is
 * the whole reason it exists as its own file:
 *
 *   **`null` renders NOTHING.** Not a zero, not a dash, not an empty "Kosten"
 *   label with a blank value. A prospect must not see a cost row at all — an
 *   empty one would tell them a number exists that they are not being shown,
 *   and a zero would be a lie about a measurement that was never taken.
 *
 * A cost object that *is* present renders even when every counter is zero: for
 * a deterministic run that never called a model, zero is a measurement.
 */
import { Coins } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { useI18n } from '@/i18n'
import { formatNumber } from '@/lib/utils'
import type { RunCost } from '@/api/types'

export function CostNote({ cost }: { cost?: RunCost | null }) {
  const { t } = useI18n()
  // The one line that matters. Nothing below it may run for a non-founder.
  if (!cost) return null

  return (
    <p
      data-testid="run-cost"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-sm border border-border bg-surface px-4 py-3 text-[12px] text-muted"
    >
      <span className="flex items-center gap-2 font-medium text-ink-2">
        <Icon icon={Coins} />
        {t('cost.title')}
      </span>
      <span>
        {t('cost.input')}: <span className="num">{formatNumber(cost.input_tokens)}</span>
      </span>
      <span>
        {t('cost.output')}: <span className="num">{formatNumber(cost.output_tokens)}</span>
      </span>
      <span>
        {t('cost.calls')}: <span className="num">{formatNumber(cost.llm_calls)}</span>
      </span>
      <span>
        {t('cost.budget')}: <span className="num">{formatNumber(cost.budget_tokens)}</span>
      </span>
      {cost.budget_exceeded ? (
        <span className="font-medium text-signal-red-text">{t('cost.exceeded')}</span>
      ) : null}
    </p>
  )
}
